// Typed-input REQUEST-price demo — extracted from Home.tsx so
// the native welcome tour shows the exact same animation as the landing page:
// cycles $5 → $20 → $50, then the same in € and £, looping, so visitors see
// we get paid out in multiple currencies. Styles live in Home.css
// (.how-price-*), which is bundled app-wide.
//
// `active` controls when the typing starts. Leave it undefined for the
// landing page behavior (starts when scrolled into view); pass a boolean to
// drive it from a slide/carousel (starts the first time it turns true).
import React, { useEffect, useRef, useState } from "react";

interface Props {
  active?: boolean;
}

const TypedPriceBox: React.FC<Props> = ({ active }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [priceValue, setPriceValue] = useState("5");
  const [currency, setCurrency] = useState("$");
  const [pulseKey, setPulseKey] = useState(0);
  const startedRef = useRef(false);
  const [started, setStarted] = useState(false);

  // In-view arming (landing page): start once at 50% visibility.
  useEffect(() => {
    if (active !== undefined) return;
    const box = boxRef.current;
    if (!box) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            setStarted(true);
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, [active]);

  // Prop arming (welcome tour): start the first time `active` turns true.
  useEffect(() => {
    if (active && !startedRef.current) {
      startedRef.current = true;
      setStarted(true);
    }
  }, [active]);

  useEffect(() => {
    if (!started) return;

    let timeoutId: number | null = null;
    let cancelled = false;

    // Timing (ms) — a touch slower than a real typist so it's easy to read
    const HOLD = 1350; // pause on a finished price
    const TYPE = 140;  // per keystroke
    const DEL = 220;   // pause while clearing

    const CURRENCIES = ["$", "€", "£"];
    const sequence: { cur: string; v: string; t: number }[] = [];
    CURRENCIES.forEach((cur) => {
      sequence.push(
        { cur, v: "5",  t: HOLD },
        { cur, v: "",   t: DEL  },
        { cur, v: "2",  t: TYPE },
        { cur, v: "20", t: HOLD },
        { cur, v: "2",  t: DEL  },
        { cur, v: "",   t: DEL  },
        { cur, v: "5",  t: TYPE },
        { cur, v: "50", t: HOLD },
        { cur, v: "5",  t: DEL  },
        { cur, v: "",   t: DEL  },
      );
    });

    const run = (i: number, prevLen: number) => {
      if (cancelled) return;
      const step = sequence[i];
      setCurrency(step.cur);
      setPriceValue(step.v);
      if (step.v.length > prevLen) {
        setPulseKey((k) => k + 1);
      }
      timeoutId = window.setTimeout(
        () => run((i + 1) % sequence.length, step.v.length),
        step.t
      );
    };

    run(0, 0);

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [started]);

  return (
    <div className="how-price-box" ref={boxRef}>
      <span className="how-price-pulse" key={pulseKey} aria-hidden />
      <div className="how-price-inner">
        <span className="how-price-cur">{currency}</span>
        <span className="how-price-value">{priceValue}</span>
        <span className="how-price-cursor" aria-hidden />
      </div>
    </div>
  );
};

export default TypedPriceBox;
